import React, { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import clsx from 'clsx'

import { Box, Grid, Typography } from '@material-ui/core'

import { useApi } from '../../hooks/storeHook'
import { useBoolState } from '../../hooks/commonHooks'
import { useMessages } from '../../hooks/snackbarHooks'
import { useSettingsPageStyles } from '../../hooks/settingsPageStylesHook'
import { MoySkladDataLoadingComponent } from '../MoySkladDataLoading'
import { MoySkladSearchableEntityInput, MsEntityDataOptional } from '../MoySkladSearchableEntityInput'

import { MobxOptionalContainer } from '../../common/mobxUtils'

import { EditStoreAccountMsDataModel } from '../../server/mpsklad_core/Models/EditStoreAccountMsDataModel'

export type MsFormData = EditStoreAccountMsDataModel

export type StoreAccountMsData = {
  organization: MsEntityDataOptional
  counterparty: MsEntityDataOptional

  contract: MsEntityDataOptional
  salesChannel: MsEntityDataOptional
  group: MsEntityDataOptional
  organizationAccount: MsEntityDataOptional
  project: MsEntityDataOptional

  readonly formData: MsFormData
}

export type CompanySettingsProps = {
  msContainer: MobxOptionalContainer<StoreAccountMsData>

  loadMsData: () => Promise<void>

  onSubmit: (msData: MsFormData) => Promise<void>
}

export const CompanySettings =
  observer(
    ({msContainer, onSubmit, loadMsData}: CompanySettingsProps) => {
      const [isLoading, setLoading, setNotLoading] = useBoolState()
      const [isMsDataLoading, setMsDataLoading, setMsStoresNotLoading] = useBoolState()

      const api = useApi()
      const classes = useSettingsPageStyles()
      const {showError} = useMessages()

      const {dataOrNull: msData} = msContainer

      useEffect(() => {
        if (!msData) {
          setMsDataLoading()
          loadMsData().finally(setMsStoresNotLoading)
        }
      }, [msData, loadMsData, setMsDataLoading, setMsStoresNotLoading])

      if (isMsDataLoading || !msData) {
        return <MoySkladDataLoadingComponent/>
      }

      const onSave = async () => {
        if (msData.formData.msOrganization === '' || msData.formData.msCounterparty === '') {
          showError('Необходимо выбрать организацию продавца и контрагент покупателя')
          return
        }

        setLoading()
        await onSubmit(msData.formData)
        setNotLoading()
      }

      return (
        <Box className={classes.pageContainer}>
          <Typography className={classes.header}>НАСТРОЙКИ ОРГАНИЗАЦИИ</Typography>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                <Grid item className={classes.root} xs={12}>
                  <Box>
                    <MoySkladSearchableEntityInput
                      isRequired
                      data={msData.organization}
                      searchEntities={api.userSync.getMoySkladOrganizations}
                      label="Организация продавца"
                      hint="Введите наименование или код для поиска."
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                <Grid item className={classes.root} xs={12}>
                  <Box>
                    <MoySkladSearchableEntityInput
                      data={msData.salesChannel}
                      searchEntities={api.userSync.getMoySkladSalesChannels}
                      label="Канал продаж"
                      hint="Введите наименование или описание для поиска."
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                <Grid item className={classes.root} xs={12}>
                  <Box>
                    <MoySkladSearchableEntityInput
                      isRequired
                      data={msData.counterparty}
                      searchEntities={api.userSync.getMoySkladCounterparties}
                      label="Контрагент покупателя"
                      hint={
                        <>
                          Введите наименование или код для поиска.
                          <br/>
                          Возможен выбор контрагента только с типом "Юридическое лицо".
                        </>
                      }
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                <Grid item className={classes.root} xs={12}>
                  <Box>
                    <MoySkladSearchableEntityInput
                      data={msData.project}
                      searchEntities={api.userSync.getMoySkladProjects}
                      label="Проект"
                      hint="Введите название проекта."
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                <Grid item className={classes.root} xs={12}>
                  <Box>
                    <MoySkladSearchableEntityInput
                      data={msData.contract}
                      searchEntities={api.userSync.getMoySkladContracts}
                      label="Договор"
                      hint={
                        <>
                          Введите номер договора или комментарий для поиска.
                          <br/>
                          Возможен выбор только договора комиссии.
                          <br/>
                          Обязательно для формирования отчётов комиссионера.
                        </>
                      }
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                <Grid item className={classes.root} xs={12}>
                  <Box>
                    <MoySkladSearchableEntityInput
                      data={msData.group}
                      searchEntities={api.userSync.getMoySkladGroups}
                      label="Отдел"
                      hint="Введите наименование отдела для поиска."
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                <Grid item className={classes.root} xs={12}>
                  <Box>
                    {/* TODO: Why not just use a select? */}
                    <MoySkladSearchableEntityInput
                      data={msData.organizationAccount}
                      searchEntities={() => Promise.resolve(msData.organizationAccount?.options ?? [])}
                      label="Расчётный счёт организации"
                      hint="Выберите счёт организации для документов и платежей."
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Box display="flex">
            <button
              className={clsx(classes.saveButton, 'default-button')}
              disabled={isLoading}
              onClick={onSave}
            >
              <p>{isLoading ? 'Сохраняем...' : 'Сохранить'}</p>
            </button>
          </Box>
        </Box>
      )
    }
  )